import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { concat, Observable, of, Subject } from 'rxjs';
import { map, tap, toArray } from 'rxjs/operators';
import { WSServiceFactory, WS_SERVICE_FACTORY } from 'types';
import { CameraService } from '../camera.service';
import { ConvertFormatOptions, PortraitServiceBase } from '../portrait.service';
import { EyePositionInfo, PhotoProcessingResult, PortraitServiceState, Position, SizeInfo } from '../hw-service-model';
import { VideoStreamDialogComponent } from './video-stream-dialog/video-stream-dialog.component';
import { QrSample } from './qr-sample';
import { HWServiceConfig } from '../hw-service-config';

@Injectable()
export class HWCameraServiceStub extends CameraService {
  private _videoStreamDialogRef: MatDialogRef<VideoStreamDialogComponent, any> | null = null;

  constructor(config: HWServiceConfig, @Inject(WS_SERVICE_FACTORY) wsFactory: WSServiceFactory, private dialog: MatDialog) {
    super(config, wsFactory);
  }

  override scanQr(x: SizeInfo): Observable<void | Uint8Array> {
    return new Observable<void | Uint8Array>((subscriber) => {
      subscriber.next();
      subscriber.next(new TextEncoder().encode(QrSample.getSample()));
      subscriber.complete();
    });
  }

  override startLocalPreviewForPhoto(x: SizeInfo, mode: 'simple' | 'smart' | 'hdr'): Observable<void> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    if (x.width !== null) {
      dialogConfig.minWidth = dialogConfig.maxWidth = x.width;
    }
    if (x.height !== null) {
      dialogConfig.minHeight = dialogConfig.maxHeight = x.height;
    }
    dialogConfig.hasBackdrop = false;
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'reset-default-dlg-style';

    this._videoStreamDialogRef = this.dialog.open(VideoStreamDialogComponent, dialogConfig);
    return of<void>(undefined);
  }

  override startMRZscan(x: SizeInfo, atrMRZup: boolean): Observable<any> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    if (x.width !== null) {
      dialogConfig.minWidth = dialogConfig.maxWidth = x.width;
    }
    if (x.height !== null) {
      dialogConfig.minHeight = dialogConfig.maxHeight = x.height;
    }
    dialogConfig.hasBackdrop = false;
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'reset-default-dlg-style';
    dialogConfig.data = 'MRZ';

    this._videoStreamDialogRef = this.dialog.open(VideoStreamDialogComponent, dialogConfig);
    return super.startMRZscan(x, atrMRZup).pipe(
      tap((r) => {
        if (r !== undefined) {
          this.closeVideoStream();
        }
      }),
    );
  }

  override stopPreview(): Observable<void> {
    this.closeVideoStream();
    return super.stopPreview();
  }

  private closeVideoStream(): void {
    this._videoStreamDialogRef?.close();
    this._videoStreamDialogRef = null;
  }
}

export class HWPortraitServiceStub extends PortraitServiceBase {
  private _stateSubject = new Subject<PortraitServiceState>();

  get connectionState$(): Observable<PortraitServiceState> {
    return this._stateSubject;
  }

  hdr(): Observable<void> {
    return of<void>(undefined);
  }

  binarizeSignature(): Observable<void> {
    throw new Error('Not implemented');
  }

  expasoft(): Observable<void> {
    return of<void>(undefined);
  }

  clear(): Observable<void> {
    return of<void>(undefined);
  }

  connect(): void {
    this._stateSubject.next(PortraitServiceState.Ready);
  }

  convertJpg2(): Observable<void> {
    return of<void>(undefined);
  }

  convert<T extends ConvertFormatOptions>(
    format: T,
    options?: T extends 'jp2' ? { rate: number } : T extends 'jpc' ? { minSize: number; maxSize: number } : never,
  ): Observable<void> {
    return of<void>(undefined);
  }

  crop(): Observable<PhotoProcessingResult> {
    return concat(this.getSampleImage('color'), this.getSampleImage('blaclkandwhite')).pipe(
      toArray(),
      map((x) => {
        const res = new PhotoProcessingResult();
        res.chipPhoto = x[0];
        res.portraitPhoto = x[1];
        return res;
      }),
    );
  }

  disconnect(): void {
    this._stateSubject.next(PortraitServiceState.Closed);
  }

  download(copy: boolean): Observable<Blob> {
    return this.getSampleImage('color');
  }

  private getSampleImage(type: 'color' | 'blaclkandwhite'): Observable<Blob> {
    return new Observable((observer) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', type === 'color' ? 'assets/images/sample_color.jpg' : 'assets/images/sample_blackandwhite.jpg', true);
      xhr.responseType = 'blob';
      xhr.onload = (e) => {
        observer.next(xhr.response);
        observer.complete();
      };
      xhr.onerror = (ev) => {
        observer.error();
      };
      xhr.send();
    });
  }

  findEyes(): Observable<EyePositionInfo> {
    const eyePositionInfo = new EyePositionInfo();
    eyePositionInfo.left = new Position(242, 275);
    eyePositionInfo.right = new Position(359, 275);
    return of(eyePositionInfo);
  }

  getID(): Observable<string> {
    return of('1');
  }

  setEyes(info: EyePositionInfo): Observable<void> {
    return of<void>(undefined);
  }

  upload(photo: Blob): Observable<void> {
    return of<void>(undefined);
  }
}
